import { Card as CardId, cardToData } from "@ravalmatic/bridge-engine/core";

export interface CardPresentation {
    x: number;
    y: number;
    angle: number;
    scaleX: number;
    scaleY: number;
    faceUp: boolean;
}

export class Card extends Phaser.GameObjects.Container {

    public static readonly WIDTH = 135;
    public static readonly HEIGHT = 190;
    private static readonly FLIP_HALF_DURATION = 150;
    private static readonly HOVER_DURATION = 130;
    private static readonly PLAY_FEEDBACK_DURATION = 250;

    private static readonly SUIT_FRAMES = [
        "spades_icon",
        "hearts_icon",
        "diamonds_icon",
        "clubs_icon",
    ];
    private static readonly RED_SUITS = [1, 2];

    private readonly frontContainer: Phaser.GameObjects.Container;
    private readonly backContainer: Phaser.GameObjects.Container;
    private readonly darkLayer: Phaser.GameObjects.Image;
    private winnerFrame?: Phaser.GameObjects.Graphics;
    public readonly cardId: CardId;
    private hoverTween?: Phaser.Tweens.Tween;
    private hoverBaseY?: number;
    private hovered = false;
    private playFeedbackEnabled = false;
    private playableCardHintsEnabled = true;
    private darkLayerTargetAlpha = 0;
    private darkLayerTween?: Phaser.Tweens.Tween;

    constructor(
        scene: Phaser.Scene, cardId: CardId, faceUp = false, interactive = false,
        private readonly onSelected?: (card: CardId) => void,
        private readonly hoverOffset = -12
    ) {

        super(scene);

        this.cardId = cardId;
        this.frontContainer = this.createFront(cardId);
        this.backContainer = this.createBack();
        this.darkLayer = new Phaser.GameObjects.Image(
            scene, 0, 0, "texture_atlas_1", "card_background_dark_layer"
        ).setAlpha(0);
        this.frontContainer.add(this.darkLayer);

        this.frontContainer.visible = faceUp;
        this.backContainer.visible = !faceUp;

        if (interactive || onSelected) {
            this.setInteractive({
                hitArea: new Phaser.Geom.Rectangle(-Card.WIDTH / 2, -Card.HEIGHT / 2, Card.WIDTH, Card.HEIGHT),
                hitAreaCallback: Phaser.Geom.Rectangle.Contains,
                useHandCursor: true,
            });
            this.on("pointerover", this.onOver, this);
            this.on("pointerout", this.onOut, this);
            this.on("pointerdown", this.onDown, this);
            if (!interactive) {
                this.disableInteractive();
            }
        }
    }

    public flip(animated = false, delay = 0, onComplete?: () => void): void {

        this.setFaceUp(true, animated, delay, onComplete);
    }

    public getPresentation(relativeTo?: Phaser.GameObjects.Container): CardPresentation {

        let matrix = this.getWorldTransformMatrix();
        if (relativeTo) {
            const inverse = relativeTo.getWorldTransformMatrix().invert();
            inverse.multiply(matrix);
            matrix = inverse;
        }
        const transform = matrix.decomposeMatrix();
        return {
            x: transform.translateX,
            y: transform.translateY,
            angle: transform.rotation * 180 / Math.PI,
            scaleX: transform.scaleX,
            scaleY: transform.scaleY,
            faceUp: this.frontContainer.visible,
        };
    }

    public unFlip(animated = false, delay = 0): void {

        this.setFaceUp(false, animated, delay);
    }

    public markAsWinner(): void {

        this.parentContainer?.bringToTop(this);
        if (this.winnerFrame) {
            return;
        }
        this.winnerFrame = new Phaser.GameObjects.Graphics(this.scene);
        this.winnerFrame.lineStyle(4, 0x43c978, 1);
        this.winnerFrame.strokeRoundedRect(
            -Card.WIDTH / 2 + 4, -Card.HEIGHT / 2 + 5,
            Card.WIDTH - 8, Card.HEIGHT - 10, 15
        );
        this.frontContainer.add(this.winnerFrame);
    }

    public resetRaisedPosition(): void {

        this.hoverTween?.stop();
        this.hoverTween = undefined;
        if (this.hoverBaseY !== undefined) {
            this.y = this.hoverBaseY;
        }
        this.hoverBaseY = undefined;
        this.hovered = false;
    }

    public setPlayFeedbackEnabled(enabled: boolean, hintsEnabled = this.playableCardHintsEnabled): void {

        this.playFeedbackEnabled = enabled;
        this.playableCardHintsEnabled = hintsEnabled;
        this.updatePlayFeedback();
    }

    public setPlayable(
        playable: boolean, feedbackEnabled = this.playFeedbackEnabled,
        hintsEnabled = this.playableCardHintsEnabled
    ): void {

        this.playFeedbackEnabled = feedbackEnabled;
        this.playableCardHintsEnabled = hintsEnabled;
        if (playable) {
            this.setInteractive();
        } else {
            this.disableInteractive();
        }
        this.updatePlayFeedback();
    }

    public destroy(fromScene?: boolean): void {

        if (!this.scene) {
            return;
        }
        this.stopAnimations();
        super.destroy(fromScene);
    }

    public stopAnimations(): void {

        if (!this.scene) {
            return;
        }
        this.scene.tweens.killTweensOf(this);
        this.scene.tweens.killTweensOf(this.frontContainer);
        this.scene.tweens.killTweensOf(this.backContainer);
        this.scene.tweens.killTweensOf(this.darkLayer);
    }

    private updatePlayFeedback(): void {

        const playable = this.input?.enabled === true;
        if (!this.playFeedbackEnabled || !playable) {
            this.resetRaisedPosition();
        }
        const alpha = this.playFeedbackEnabled && this.playableCardHintsEnabled && !playable ? 1 : 0;
        if (alpha === this.darkLayerTargetAlpha) {
            return;
        }
        this.darkLayerTargetAlpha = alpha;
        this.darkLayerTween?.stop();
        this.darkLayerTween = undefined;
        if (this.darkLayer.alpha !== alpha) {
            this.darkLayerTween = this.scene.tweens.add({
                targets: this.darkLayer,
                alpha,
                duration: Card.PLAY_FEEDBACK_DURATION,
                ease: "Cubic.Out",
                onComplete: () => { this.darkLayerTween = undefined; }
            });
        }
    }

    private setFaceUp(faceUp: boolean, animated: boolean, delay: number, onComplete?: () => void): void {

        if (!faceUp) {
            this.resetRaisedPosition();
        }
        const incoming = faceUp ? this.frontContainer : this.backContainer;
        const outgoing = faceUp ? this.backContainer : this.frontContainer;
        this.scene.tweens.killTweensOf(incoming);
        this.scene.tweens.killTweensOf(outgoing);

        if (!animated || (incoming.visible && incoming.scaleX === 1)) {
            incoming.setVisible(true);
            outgoing.setVisible(false);
            incoming.scaleX = 1;
            outgoing.scaleX = 1;
            onComplete?.();
            return;
        }

        const expand = () => {
            this.scene.tweens.add({
                targets: incoming,
                scaleX: 1,
                duration: Card.FLIP_HALF_DURATION,
                delay: incoming.visible ? delay : 0,
                ease: "Sine.Out",
                onComplete,
            });
            incoming.setVisible(true);
        };

        if (incoming.visible) {
            expand();
            return;
        }

        incoming.scaleX = 0;
        this.scene.tweens.add({
            targets: outgoing,
            scaleX: 0,
            duration: Card.FLIP_HALF_DURATION,
            delay,
            ease: "Sine.In",
            onComplete: () => {
                outgoing.setVisible(false);
                outgoing.scaleX = 1;
                expand();
            },
        });
    }

    private onOver(): void {

        if (!this.playFeedbackEnabled || !this.input?.enabled || !this.frontContainer.visible
            || this.hoverOffset === 0 || this.hovered) {
            return;
        }
        this.hovered = true;
        this.hoverBaseY ??= this.y;
        this.tweenHover(this.hoverBaseY + this.hoverOffset);
    }

    private onOut(): void {

        if (!this.hovered || this.hoverBaseY === undefined) {
            return;
        }
        this.hovered = false;
        this.tweenHover(this.hoverBaseY);
    }

    private tweenHover(y: number): void {

        this.hoverTween?.stop();
        this.hoverTween = this.scene.tweens.add({
            targets: this,
            y,
            duration: Card.HOVER_DURATION,
            ease: "Cubic.Out",
            onComplete: () => {
                this.hoverTween = undefined;
                if (!this.hovered) {
                    this.hoverBaseY = undefined;
                }
            }
        });
    }

    private onDown(): void {

        if (this.input?.enabled && this.frontContainer.visible) {
            this.onSelected?.(this.cardId);
        }
    }

    private createFront(card: CardId): Phaser.GameObjects.Container {

        const cardData = cardToData(card);
        const suitFrame = Card.SUIT_FRAMES[cardData.suit];
        const color = Card.RED_SUITS.includes(cardData.suit) ? "#D92727" : "#1F252D";

        const frontContainer = new Phaser.GameObjects.Container(this.scene);
        this.add(frontContainer);

        const cardBackground = new Phaser.GameObjects.Image(this.scene, 0, 0, "texture_atlas_1", "card_background");
        frontContainer.add(cardBackground);

        const rankLabel = new Phaser.GameObjects.Text(this.scene, -34, -62, cardData.value, {
            fontFamily: "Baloo2-SemiBold",
            fontSize: cardData.value === "10" ? "39px" : "44px",
            color,
        });
        rankLabel.setOrigin(.5);
        frontContainer.add(rankLabel);

        const smallSuitCorner = new Phaser.GameObjects.Image(this.scene, -34, -28, "texture_atlas_1", suitFrame);
        smallSuitCorner.setScale(.175);
        frontContainer.add(smallSuitCorner);

        const bigSuitIcon = new Phaser.GameObjects.Image(this.scene, 0, 28, "texture_atlas_1", suitFrame);
        bigSuitIcon.setScale(.495);
        frontContainer.add(bigSuitIcon);

        return frontContainer;
    }

    private createBack(): Phaser.GameObjects.Container {

        const backContainer = new Phaser.GameObjects.Container(this.scene);
        this.add(backContainer);

        const backImage = new Phaser.GameObjects.Image(this.scene, 0, 0, "texture_atlas_1", "back_card");
        backContainer.add(backImage);

        return backContainer;
    }
}
